import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { api } from '../api/client';
import { formatDateTime } from '../utils/format';

interface LiveRates {
  source: string | null;
  live: boolean;
  fetchedAt: string;
  rates: { currencyCode: string; inr: string; changePercent: string | null; asOf: string }[];
}

const SYMBOL: Record<string, string> = { USD: '$', GBP: '£', EUR: '€' };

/**
 * Live $ / £ / € → ₹ rates for reference. Entries keep using the month's
 * configured rate; these are never saved or used in calculations.
 */
export function LiveRatesStrip({ className }: { className?: string }) {
  const { data } = useQuery({
    queryKey: ['fx-live'],
    queryFn: () => api.get<LiveRates>('/fx/live'),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
  if (!data?.rates.length) return null;
  const asOf = data.rates
    .map((r) => r.asOf)
    .sort()
    .pop();
  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600',
        className,
      )}
    >
      <span className="flex items-center gap-1.5 font-medium text-slate-500">
        {data.live && <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden />}
        {data.live ? 'Live rate' : 'Today’s rate'}
      </span>
      {data.rates.map((r) => {
        const change = r.changePercent == null ? null : Number(r.changePercent);
        return (
          <span
            key={r.currencyCode}
            className="num inline-flex items-center gap-1 whitespace-nowrap"
          >
            <span className="text-slate-500">{SYMBOL[r.currencyCode] ?? r.currencyCode}1 =</span>
            <span className="font-semibold text-slate-900">₹{Number(r.inr).toFixed(2)}</span>
            {change != null && change !== 0 && (
              <span
                className={clsx(
                  'inline-flex items-center text-[11px]',
                  change > 0 ? 'text-emerald-600' : 'text-rose-600',
                )}
              >
                {change > 0 ? (
                  <TrendingUp className="size-3" aria-hidden />
                ) : (
                  <TrendingDown className="size-3" aria-hidden />
                )}
                {Math.abs(change).toFixed(2)}%
              </span>
            )}
          </span>
        );
      })}
      <span className="text-[11px] text-slate-400">
        {data.source} · {formatDateTime(asOf)} · reference only
      </span>
    </div>
  );
}
